// Gramsci's rich-text fields, turned into something a page can render.
//
// Descriptions and share messages are authored in a WYSIWYG editor and arrive
// as HTML ("<p>…</p>", entity-encoded). Two consumers, two needs: the page
// renders them as markup (htmlToParagraphs), while share buttons and og: tags
// take plain text only (htmlToPlainText).
//
// Extracted from CampaignPage so the petition page renders the same fields the
// same way — the descriptions come from the same editor whichever kind of
// record carries them.

// A paragraph can carry inline markup (<strong>, links). It goes through the
// allowlist below first: anything not inline formatting is unwrapped to its
// text, and every attribute is dropped except a link's.
const INLINE_TAGS = new Set(['STRONG', 'B', 'EM', 'I', 'U', 'SPAN', 'BR', 'A', 'SMALL', 'SUB', 'SUP']);
// Removed with their contents — unwrapping these would print their source text.
const DROP_TAGS = new Set(['SCRIPT', 'STYLE', 'IFRAME', 'OBJECT', 'EMBED', 'TEMPLATE', 'NOSCRIPT']);
const SAFE_HREF = /^(https?:|mailto:|tel:|\/|#)/i;

// Strip `el` down to the allowlist and return its inner HTML.
export function sanitizeInline(el) {
  el.querySelectorAll('*').forEach((node) => {
    const tag = node.tagName;
    if (DROP_TAGS.has(tag)) return node.remove();
    if (!INLINE_TAGS.has(tag)) return node.replaceWith(...node.childNodes);

    // Read href before the wipe, then put back only the three link attributes.
    const href = tag === 'A' ? (node.getAttribute('href') || '').trim() : '';
    Array.from(node.attributes).forEach((a) => node.removeAttribute(a.name));
    if (tag === 'A' && SAFE_HREF.test(href)) {
      node.setAttribute('href', href);
      node.setAttribute('target', '_blank');
      node.setAttribute('rel', 'noopener noreferrer');
    }
  });
  return el.innerHTML.trim();
}

// Plain text destined for the same innerHTML slot has to be escaped.
export const escapeHtml = (t) => {
  const d = document.createElement('div');
  d.textContent = t;
  return d.innerHTML;
};

// Tag of the single inline element wrapping a whole block, if any. Replacing this with
// CSS :only-child would also style "<p><strong>a</strong> b</p>", since it ignores bare text.
function soleWrapper(el) {
  const text = el.textContent.trim();
  const child = el.firstElementChild;
  if (!text || el.children.length !== 1 || child.textContent.trim() !== text) return undefined;
  return child.tagName.toLowerCase();
}

// Rich text -> [{ tag, html, only? }] blocks, or null when there is nothing to show.
// Each block keeps its inline markup so bold/italic/links survive to the page,
// and its own tag so a heading still renders (and styles) as a heading instead
// of flattening into a <p> — Gramsci's editor emits an <h3> for the closing
// call to action, and querying <p> alone dropped it. DOMParser never executes
// scripts. Plain text falls back to newline splitting.
export function htmlToParagraphs(s) {
  if (typeof s !== 'string' || !s.trim()) return null;
  // Some backend rows contain malformed paragraph closers ("<\p>" / "<\/p>").
  // HTML parsers treat those as literal text, so normalize them first.
  const normalized = s.replace(/<\\\/?\s*p\s*>/gi, '</p>');
  const doc = new DOMParser().parseFromString(normalized, 'text/html');
  const fromTags = Array.from(doc.querySelectorAll('p, h1, h2, h3, h4, h5, h6, li'))
    .map((el) => {
      const tag = el.tagName.toLowerCase();
      // Keep intentionally blank paragraphs unsanitized so editor-authored
      // spacing (empty lines) survives exactly as entered.
      //
      // The non-breaking space stays written as \u00a0, never as the character
      // itself. A blank line from the editor is a paragraph holding one
      // &nbsp;, so this is what decides whether one counts as blank — and as a
      // literal it is invisible in every diff and editor, indistinguishable
      // from the ordinary space beside it, and one whitespace-tidying pass
      // would quietly turn it into one.
      if (tag === 'p' && !el.querySelector('*') && !el.textContent.replace(/\u00a0/g, '').trim()) {
        return { tag, html: el.innerHTML };
      }
      const html = sanitizeInline(el);
      const only = soleWrapper(el);
      return only ? { tag, html, only } : { tag, html };
    })
    // An empty <p> is kept — that is the blank line above. Other empty blocks
    // (a heading or list item with nothing in it) are still dropped.
    .filter((b) => b.tag === 'p' || b.html);
  const out = fromTags.length
    ? fromTags
    : doc.body.textContent
        .split(/\r?\n+/)
        .map((t) => ({ tag: 'p', html: escapeHtml(t.trim()) }))
        .filter((b) => b.html);
  return out.length ? out : null;
}

// Rich text -> one line of plain text, tags and entities resolved. For places
// that can't take markup: WhatsApp/X share messages, og: description tags.
// null when there is nothing, so callers can fall back to their own wording.
export function htmlToPlainText(s) {
  if (typeof s !== 'string' || !s.trim()) return null;
  const doc = new DOMParser().parseFromString(s, 'text/html');
  const text = doc.body.textContent.replace(/\s+/g, ' ').trim();
  return text || null;
}
